import { useState, useEffect, useCallback, useRef, useContext } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { MdDownload, MdPlayArrow, MdPlayCircle, MdPlayCircleOutline } from "react-icons/md";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { setImageUrl } from "../utils";
import { AccountContext } from "../contexts/AccountContexts";
import { darkAndLightThemeDefault } from "./ThemeComponents";
import { BACKEND_URI } from "../entries";
import { useTranslation } from "react-i18next";


const ImageViewer = ({ imageUrls }) => {
    const { language } = useContext(AccountContext)
    const { t } = useTranslation("edit")
    const [isModalOpen, setModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false); // Track hover state
    const [zoomedIn, setZoomedIn] = useState(false)
    const [zoomPosition, setZoomPosition] = useState({ x: 0.5, y: 0.5 });
    const [zoomLevel, setZoomLevel] = useState(2);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });

    const openModal = (index) => {
        setCurrentImageIndex(index);
        setModalOpen(true);
    };

    const closeModal = () => setModalOpen(false);

    const nextImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === imageUrls.length - 1 ? 0 : prevIndex + 1
        );
    };

    const prevImage = () => {
        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? imageUrls.length - 1 : prevIndex - 1
        );
    };

    const handleOutsideClick = (event) => {
        if (event.target.classList.contains('modal-overlay')) {
            closeModal();
        }
    };

    const handleKeyDown = useCallback(
        (event) => {
            if (event.key === 'ArrowRight') {
                nextImage();
            } else if (event.key === 'ArrowLeft') {
                prevImage();
            } else if (event.key === 'Escape') {
                setZoomedIn(false)
                setZoomLevel(2);
                closeModal();
            }
        },
        [nextImage, prevImage]
    );

    useEffect(() => {

        if (isModalOpen) {
            document.addEventListener('keydown', handleKeyDown);
        } else {
            document.removeEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isModalOpen, handleKeyDown]);

    const imgRef = useRef(null)


    const [dimensions, setDimensions] = useState({ height: 0, width: 420 })


    const normalizeImage = (height, width, scaler = 1.0) => {
        const windowHeight = window.innerHeight
        const windowWidth = window.innerWidth
        if (windowHeight < height && windowWidth > width) {
            return { height: scaler * windowHeight, width: scaler * (windowHeight / height) * width }
        }
        else if (windowHeight > height && windowWidth < width) {
            return { height: scaler * (windowWidth / width) * height, width: scaler * windowWidth }
        }
        else if (windowHeight > height && windowWidth > width) {
            return { height: height, width: width }
        }
        else {
            // When both h and w of the image are over charts. Calculate which is the biggest wrt window counterpart and normalize according to it

            if ((windowHeight / height) * width > windowWidth) {
                return { height: scaler * (windowWidth / width) * height, width: scaler * windowWidth }
            }
            else {
                return { height: scaler * windowHeight, width: scaler * (windowHeight / height) * width }
            }
        }
    }
    const zoomRef = useRef(null)
    const handleImageLoad = (e) => {
        const isVideo = e.target.tagName === 'VIDEO'
        const { naturalHeight, naturalWidth } = e.target;
        const { videoHeight, videoWidth } = e.target
        setDimensions((prev) => { return normalizeImage(isVideo ? videoHeight : naturalHeight, isVideo ? videoWidth : naturalWidth, 0.9) });
    }



    const handleZoomIn = (e) => {
        setZoomedIn((prev) => !prev)
        if (!zoomedIn) {
            const { offsetX, offsetY, target } = e.nativeEvent;
            setZoomPosition({
                x: offsetX / target.width,
                y: offsetY / target.height,
            });
        }
        else {
            setZoomLevel(2)
        }
    }

    const getBoundedPosition = (x, y) => {
        const visibleAreaRatio = 1 / zoomLevel;
        const maxOffset = 1 - visibleAreaRatio;

        return {
            x: Math.min(maxOffset, Math.max(visibleAreaRatio / 2, x)),
            y: Math.min(maxOffset, Math.max(visibleAreaRatio / 2, y)),
        };
    };

    const handleMouseDown = (e) => {
        if (e.button === 2 && zoomedIn) { // Right-click
            e.preventDefault();
            setIsDragging(true);
            setDragStartPosition({ x: e.clientX, y: e.clientY });
        }
    };

    const handleMouseMove = (e) => {

        if (isDragging && zoomedIn) {
            const deltaX = (e.clientX - dragStartPosition.x) / 300 / zoomLevel; // Adjust divisor for sensitivity
            const deltaY = (e.clientY - dragStartPosition.y) / 300 / zoomLevel;


            setZoomPosition((prev) => ({
                x: Math.min(1.5, Math.max(-0.5, prev.x - deltaX)), // Clamp between 0 and 1
                y: Math.min(1.5, Math.max(-0.5, prev.y - deltaY)),
            }));
            // setZoomPosition((prev) => {
            //     const newPosition = {
            //         x: prev.x - deltaX,
            //         y: prev.y - deltaY,
            //     };
            //     return getBoundedPosition(newPosition.x, newPosition.y); // Apply bounds
            // });
            setDragStartPosition({ x: e.clientX, y: e.clientY });
        }

    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleWheel = (e) => {
        e.preventDefault();
        if (zoomedIn) {
            const { offsetX, offsetY, target } = e.nativeEvent;
            setZoomPosition({
                x: offsetX / target.width,
                y: offsetY / target.height,
            });
            const newZoomLevel = zoomLevel + (e.deltaY < 0 ? 0.1 : -0.1);
            setZoomLevel(Math.min(5, Math.max(1.2, newZoomLevel))); // Clamp between 200% and 500%
        }
    };

    useEffect(() => {
        window.addEventListener('mouseup', handleMouseUp);
        return () => window.removeEventListener('mouseup', handleMouseUp);
    }, []);

    const handleDownload = () => {
        let currImage = imageUrls[currentImageIndex]
        const filename = currImage.split('/').pop();

        // Replace with your backend URL
        const backendUrl = `${BACKEND_URI}/download/${filename}`;
        // Create a temporary link element
        const link = document.createElement("a");
        link.href = backendUrl;
        link.download = filename; // Suggested filename for the browser
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };



    return (
        <div className="image-viewer text-gray-600">

            {/* Thumbnail Previews */}
            <div className="flex gap-2 flex-wrap">
                {imageUrls && imageUrls.length > 0 && imageUrls.map((url, index) => {
                    const isDeleted = url === "DELETED";
                    const isImage = /\.(jpg|jpeg|png|gif|bmp|svg|webp)$/i.test(url);
                    const isVideo = /\.(mp4)$/i.test(url);
                    const fileName = url.split('/').pop(); // Get the file name from the URL
                    const fileExtension = fileName.split('.').pop().toUpperCase(); // Get the extension
                    

                    return (
                        <div key={index} className="w-16 h-16 cursor-pointer rounded-md flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-800" onClick={() => !isDeleted && openModal(index)}>
                            {
                                isDeleted
                                    ?
                                    <div className="flex flex-col items-center">
                                        <div className="text-2xl text-gray-600 dark:text-gray-500">🗑️</div>
                                        <p className="text-xs text-center truncate max-w-full w-16 px-1">{t("Deleted")}</p>

                                    </div>
                                    :
                                    (isImage ? (
                                        <img
                                            src={setImageUrl(url)}
                                            alt={`attachment-${index}`}
                                            loading="lazy"
                                            width={64}
                                            height={64}
                                            className="w-full h-full object-cover rounded-md"
                                        />
                                    ) : isVideo ? (
                                        <div className="relative w-full h-full">
                                        <MdPlayCircleOutline className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-5xl text-white hover:text-gray-400 z-10" />
                                        <video
                                            src={setImageUrl(url)}
                                            alt={`attachment-${index}`}
                                            width={64}
                                            height={64}
                                            className="w-full h-full object-cover rounded-md"
                                            loop
                                        />
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center">
                                            <div className="text-2xl text-gray-600 dark:text-gray-500">📄</div>
                                            <span className="text-xs font-medium">{fileExtension}</span>
                                            <p className="text-xs text-center truncate max-w-full w-16 px-1">{fileName}</p>
                                        </div>
                                    ))}
                        </div>
                    );
                }
                )}
            </div>
            {
                zoomedIn &&
                <div className="modal modal-open modal-overlay z-100" onClick={handleZoomIn} onMouseMove={handleMouseMove} onMouseDown={handleMouseDown} onContextMenu={(e) => e.preventDefault()} onWheel={handleWheel}>

                    <div className=" z-50 relative">
                        <img src={setImageUrl(imageUrls[currentImageIndex])}
                            className={`transition-transform duration-300
                                ${isDragging ? "cursor-grabbing" : "cursor-zoom-out"}`}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                transformOrigin: `${zoomPosition.x * 100}% ${zoomPosition.y * 100}%`,
                                transform: `scale(${zoomLevel})`,
                                zIndex: 100,
                                position: 'relative',
                            }}
                            ref={zoomRef}
                        />
                    </div>
                </div>

            }
            {/* Modal for Enlarged Image */}
            {isModalOpen && !zoomedIn && (
                <div
                    className="modal modal-open modal-overlay flex justify-center items-center text-gray-800 dark:text-gray-300"
                    onClick={handleOutsideClick}
                >
                    <div
                        className={`modal-box relative flex items-center justify-center p-0 w-max max-w-[${dimensions.width}px]`}
                        style={{ width: "max-content", maxWidth: dimensions.width }}
                        onMouseEnter={() => setIsHovered(true)} // Show buttons on hover
                        onMouseLeave={() => setIsHovered(false)} // Hide buttons when not hovering
                    >
                        <button
                            className={`btn btn-sm z-50 btn-circle ${darkAndLightThemeDefault} absolute ltr:right-2 rtl:left-2 top-2 transform  transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'
                                }`}
                            onClick={closeModal}
                        >
                            <IoCloseOutline size={24} />
                        </button>

                        <button
                            className={`btn btn-sm z-50 btn-circle ${darkAndLightThemeDefault} absolute ltr:right-11 rtl:left-11 top-2 transform transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'
                                }`}
                            onClick={handleDownload}
                        >
                            <MdDownload size={20} />
                        </button>

                        {/* Previous arrow (show only on hover, with transition) */}
                        <button
                            className={`btn btn-circle z-50 btn-sm ${darkAndLightThemeDefault} absolute ltr:left-1 rtl:right-1 top-1/2 transform transition-opacity duration-100 ${isHovered ? 'opacity-100' : 'opacity-0'
                                }`}
                            onClick={prevImage}
                        >
                            {
                                language === "ar" ?
                                    <FaArrowRight />
                                    :
                                    <FaArrowLeft />
                            }
                        </button>

                        {/* Image */}
                        {
                            [setImageUrl(imageUrls[currentImageIndex])].map((url) => {
                                const isImage = /\.(jpg|jpeg|png|gif|bmp|svg|webp)$/i.test(url);
                                const fileName = url.split('/').pop(); // Get the file name from the URL
                                const fileExtension = fileName.split('.').pop().toUpperCase(); // Get the extension
                                const isVideo = /\.(mp4)$/i.test(url);

                                return (
                                    isImage ? (
                                        <img
                                            src={url}
                                            // alt={`attachment-${index}`}
                                            alt="enlarged view"
                                            className="h-auto w-auto rounded-lg cursor-zoom-in"
                                            onLoad={handleImageLoad}
                                            onClick={handleZoomIn}
                                            ref={imgRef}
                                        />
                                    ) : isVideo ? (
                                        <video
                                            src={url}
                                            controls
                                            className="rounded-lg cursor-pointer"
                                            onLoadedData={handleImageLoad}
                                            ref={imgRef}
                                        />
                                    ) : (
                                        <div className="flex flex-col items-center gap-1 py-1">
                                            <div className="text-6xl text-gray-600 dark:text-gray-500">📄</div> {/* Icon placeholder, replace with actual icon if needed */}
                                            <span className="text-lg font-medium">{fileExtension}</span>
                                            <p className="text-lg text-center truncate max-w-full px-1">{fileName}</p>
                                        </div>
                                    )
                                )
                            })

                        }

                        {/* Next arrow (show only on hover, with transition) */}
                        <button
                            className={`btn btn-circle btn-sm ${darkAndLightThemeDefault} absolute ltr:right-1 rtl:left-1 top-1/2 transform  transition-opacity duration-100 ${isHovered ? 'opacity-100' : 'opacity-0'
                                }`}
                            onClick={nextImage}
                        >
                            {
                                language === "ar" ?
                                    <FaArrowLeft />
                                    :
                                    <FaArrowRight />
                            }
                        </button>
                    </div>
                </div>
            )
            }
        </div >
    );
};



export default ImageViewer;